import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, MapPin, Video, User, Play } from 'lucide-react';
import { format, differenceInMinutes } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface ConsultationReminder {
  id: string;
  clientName: string;
  consultationType: 'in-person' | 'virtual';
  date: Date;
  time: string;
  location?: string;
  meetLink?: string;
  notes?: string;
}

interface ConsultationReminderPopupProps {
  reminder: ConsultationReminder;
  onDismiss: (id: string) => void;
}

export const ConsultationReminderPopup: React.FC<ConsultationReminderPopupProps> = ({ reminder, onDismiss }) => {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onDismiss(reminder.id);
    }, 300);
  };

  const handleStartConsultation = () => {
    // Navigate to consultations page with the consultation ID to start
    navigate(`/consultations?start=${reminder.id}`);
    handleClose();
  };

  const minutesUntil = differenceInMinutes(reminder.date, new Date());
  const urgencyClass = minutesUntil <= 15 ? 'border-red-500 bg-red-50' : 'border-amber-500 bg-amber-50';
  const urgencyText = minutesUntil <= 15 ? 'Starting Soon!' : minutesUntil <= 60 ? '1 Hour Reminder' : 'Upcoming Consultation';

  return (
    <div
      className={`
        fixed bottom-4 right-4 z-50 max-w-md
        transition-all duration-300 ease-out
        ${isVisible && !isClosing ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
    >
      <div className={`
        bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl border-2 ${urgencyClass}
        overflow-hidden
      `}>
        {/* Header */}
        <div className={`px-4 py-3 border-b ${minutesUntil <= 15 ? 'bg-red-100' : 'bg-amber-100'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className={`
                p-1.5 rounded-full ${minutesUntil <= 15 ? 'bg-red-500' : 'bg-amber-500'}
                animate-pulse
              `}>
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <span className={`font-semibold ${minutesUntil <= 15 ? 'text-red-900' : 'text-amber-900'}`}>
                {urgencyText}
              </span>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded-lg hover:bg-white/50 transition-colors"
            >
              <X className="w-4 h-4 text-neutral-600" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Client Name */}
          <div className="flex items-center space-x-3">
            <User className="w-5 h-5 text-neutral-600" />
            <div>
              <p className="text-sm text-neutral-600">Client</p>
              <p className="font-semibold text-neutral-900">{reminder.clientName}</p>
            </div>
          </div>

          {/* Time */}
          <div className="flex items-center space-x-3">
            <Clock className="w-5 h-5 text-neutral-600" />
            <div>
              <p className="text-sm text-neutral-600">Time</p>
              <p className="font-semibold text-neutral-900">
                {reminder.time} ({minutesUntil} minutes)
              </p>
            </div>
          </div>

          {/* Location/Link */}
          {reminder.consultationType === 'virtual' ? (
            <div className="flex items-center space-x-3">
              <Video className="w-5 h-5 text-blue-600" />
              <div className="flex-1">
                <p className="text-sm text-neutral-600">Virtual Meeting</p>
                {reminder.meetLink && (
                  <a
                    href={reminder.meetLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
                  >
                    Join Google Meet →
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-neutral-600" />
              <div>
                <p className="text-sm text-neutral-600">Location</p>
                <p className="font-semibold text-neutral-900 text-sm">
                  {reminder.location || 'Office'}
                </p>
              </div>
            </div>
          )}

          {/* Notes */}
          {reminder.notes && (
            <div className="pt-2 border-t border-neutral-200">
              <p className="text-xs text-neutral-600 mb-1">Notes</p>
              <p className="text-sm text-neutral-700">{reminder.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-4 pb-4 flex gap-2">
          <button
            onClick={handleClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 hover:bg-neutral-200 rounded-lg transition-colors"
          >
            Dismiss
          </button>
          <button
            onClick={handleStartConsultation}
            className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Play className="w-4 h-4" />
            <span>Start Consultation</span>
          </button>
        </div>
      </div>
    </div>
  );
};